import { environment } from '../../../environments/environment';

/**
 * Partner API calls routed through prism-new's `/partner/proxy/<key>/<path>`, which attaches the
 * partner's stored credentials server-side so the browser never holds them. Active only while
 * `environment.prismNew.enabled` is on; when it is off every caller keeps its direct partner call.
 */

/**
 * How long a proxied partner call may take. prism-new waits up to 120 s on the partner (report
 * endpoints run long), so the browser waits a little longer to receive that answer or its error.
 */
export const PARTNER_PROXY_TIMEOUT_MS = 130_000;

/** productServerName → the proxy key (the product's keyname). Anything absent is never proxied. */
const PARTNER_PROXY_KEYS: Readonly<Record<string, string>> = {
  denefits: 'denefits',
  lendee: 'lendee',
  credee: 'credee',
  'cool-credit': 'cool_credit',
  practina: 'practina',
  'finance-mutual': 'finance_mutual',
  recuvery: 'recuvery',
};

/** The proxy key for a partner server, or null when the call must go direct. */
export function partnerProxyKey(serverName: string | null | undefined): string | null {
  if (!environment.prismNew.enabled || !serverName) return null;
  return PARTNER_PROXY_KEYS[serverName] ?? null;
}

/** `base` + `url` the way axios combines them; an absolute `url` wins. */
export function joinUrl(base: string, url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
}

function pathOf(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
  } catch {
    return '';
  }
}

/**
 * The proxy URL for a call the CRM would otherwise send to `targetUrl`. The backend resolves the
 * path beneath the partner's stored base URL, so the part of the target already under that base
 * (`relativeTo`, from get-setting) is dropped. A target on the same host but outside that base
 * (a partner's exports, say) is sent as `~/<path>`, which the backend resolves at the host root.
 */
export function partnerProxyUrl(key: string, targetUrl: string, relativeTo?: string | null): string {
  const target = new URL(targetUrl, window.location.origin);
  const basePath = relativeTo ? pathOf(relativeTo) : '';
  let path = target.pathname;
  if (basePath && path.startsWith(`${basePath}/`)) {
    path = path.slice(basePath.length);
  } else if (basePath) {
    path = `/~${path}`;
  }
  const root = environment.prismNew.apiUrl.replace(/\/+$/, '');
  return `${root}/partner/proxy/${key}/${path.replace(/^\/+/, '')}${target.search}`;
}
